import { FC, useState } from "react";

import { Stack } from "@mui/material";
import { Emptyable } from "@simplyk/common";

import { ContactSupportLink } from "../ContactSupportLink";
import { SixDigitsCode } from "../SMSCodeStep/SixDigitsCode";

import { ResendCodeButton } from "./ResendCodeButton/ResendCodeButton";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { TwoFactorMethod, TwoFactorSubmitResult } from "@/common/contexts/TwoFactorContext";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardCheckbox } from "@/dashboard-design-system/DashboardCheckbox";
import {
  DashboardDialog,
  DashboardDialogBody,
  DashboardDialogContent,
  DashboardDialogFooter,
  DashboardDialogHeader,
} from "@/dashboard-design-system/DashboardDialog";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardTooltip";
import { DashboardLink } from "@/dashboard-design-system/Link";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Lock as LockIcon } from "@/icons/outlined";

export const TWO_FACTOR_MODAL_Z_INDEX = 999999999;

export interface TwoFactorVerificationModalProps {
  open: boolean;
  preferredMethod: TwoFactorMethod;
  maskedPhoneNumber?: string;
  hasSmsFallback?: boolean;
  isSmsVerificationNext?: boolean;
  onSubmit: (
    code: string,
    method: TwoFactorMethod,
    options?: { askForBypassMfa?: boolean }
  ) => Promise<TwoFactorSubmitResult>;
  onRequestSmsFallback?: () => Promise<{ maskedPhoneNumber?: string } | { error: string } | null>;
  onCancel: () => void;
}

export const TwoFactorVerificationModal: FC<TwoFactorVerificationModalProps> = ({
  open,
  preferredMethod,
  maskedPhoneNumber: initialMaskedPhoneNumber,
  hasSmsFallback,
  isSmsVerificationNext,
  onSubmit,
  onRequestSmsFallback,
  onCancel,
}) => {
  const { t } = useTranslate();
  const { currentUser, connectedAsAdministrator } = useCurrentUserContext();
  const [code, setCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSwitchingToSms, setIsSwitchingToSms] = useState(false);
  const [error, setError] = useState<Emptyable<string>>(undefined);
  const [activeMethod, setActiveMethod] = useState<TwoFactorMethod>(preferredMethod);
  const [maskedPhoneNumber, setMaskedPhoneNumber] = useState<string | undefined>(initialMaskedPhoneNumber);
  const [askForBypassMfa, setAskForBypassMfa] = useState(false);
  const [isUsingRecoveryCode, setIsUsingRecoveryCode] = useState(false);

  const isRecoveryCodeMode = activeMethod === "totp" && isUsingRecoveryCode;
  const isCodeComplete = isRecoveryCodeMode ? code.trim().length > 0 : code.length === 6;
  const canSubmit = isCodeComplete || askForBypassMfa;

  const invalidCodeError =
    activeMethod === "sms"
      ? t("common", "smsCode.errorInvalidCode")
      : isRecoveryCodeMode
        ? t("dashboard", "twoFactor.totp.errorInvalidRecoveryCode")
        : t("dashboard", "twoFactor.totp.errorInvalidCode");

  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }

    if (!canSubmit) {
      setError(t("common", "smsCode.errorCodeRequired"));
      return;
    }

    setIsSubmitting(true);
    setError(undefined);

    try {
      const result = await onSubmit(isRecoveryCodeMode ? code.trim() : code, activeMethod, { askForBypassMfa });

      if (result && "switchToSms" in result) {
        setActiveMethod("sms");
        setMaskedPhoneNumber(result.switchToSms.maskedPhoneNumber);
        setCode("");
        setIsSubmitting(false);
        return;
      }

      if (result && "error" in result) {
        setError(invalidCodeError);
        setIsSubmitting(false);
      }
    } catch {
      setError(invalidCodeError);
      setIsSubmitting(false);
    }
  };

  const toggleRecoveryCode = () => {
    setIsUsingRecoveryCode(!isUsingRecoveryCode);
    setCode("");
    setError(undefined);
  };

  const handleCancel = () => {
    setCode("");
    setError(undefined);
    setActiveMethod(preferredMethod);
    setAskForBypassMfa(false);
    setIsUsingRecoveryCode(false);
    onCancel();
  };

  const handleRequestSmsFallback = async () => {
    if (!onRequestSmsFallback) {
      return;
    }

    setIsSwitchingToSms(true);
    setError(undefined);
    setCode("");
    setIsUsingRecoveryCode(false);

    const result = await onRequestSmsFallback();
    if (result && "error" in result) {
      setError(result.error);
    } else if (result && "maskedPhoneNumber" in result) {
      setMaskedPhoneNumber(result.maskedPhoneNumber);
      setActiveMethod("sms");
    }

    setIsSwitchingToSms(false);
  };

  const authenticatorTitle = isSmsVerificationNext
    ? t("dashboard", "twoFactor.sms.activation.identityStepTitle")
    : t("dashboard", "twoFactor.totp.verifyTitle");
  const totpTitle = isRecoveryCodeMode ? t("dashboard", "twoFactor.totp.recoveryCodeTitle") : authenticatorTitle;
  const smsTitle = isSmsVerificationNext
    ? t("dashboard", "twoFactor.sms.activation.phoneStepTitle")
    : t("common", "smsCode.title");
  const title = activeMethod === "totp" ? totpTitle : smsTitle;

  const authenticatorDescription = isSmsVerificationNext
    ? t("dashboard", "twoFactor.sms.activation.identityStepDescription")
    : t("dashboard", "twoFactor.totp.verifyDescription");
  const totpDescription = isRecoveryCodeMode
    ? t("dashboard", "twoFactor.totp.recoveryCodeDescription")
    : authenticatorDescription;

  const description =
    activeMethod === "totp"
      ? totpDescription
      : maskedPhoneNumber
        ? t("common", "smsCode.descriptionWithPhone", { phone: maskedPhoneNumber })
        : t("common", "smsCode.description");

  return (
    <DashboardDialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      vibe="brand"
      sx={{ zIndex: TWO_FACTOR_MODAL_Z_INDEX }}
    >
      <DashboardDialogBody
        slots={{
          header: <DashboardDialogHeader icon={<LockIcon />} title={title} slotProps={{ closeButton: undefined }} />,
          content: (
            <DashboardDialogContent>
              <Stack spacing={3}>
                <Stack spacing={2}>
                  <DashboardTypography variant="body2">{description}</DashboardTypography>

                  {isRecoveryCodeMode ? (
                    <DashboardTextField
                      autoFocus
                      size="medium"
                      value={code}
                      onChange={(event) => setCode(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          void handleSubmit();
                        }
                      }}
                      inputProps={{
                        maxLength: 64,
                        autoComplete: "one-time-code",
                        spellCheck: false,
                        "aria-label": t("dashboard", "twoFactor.totp.recoveryCodeTitle"),
                      }}
                      data-test="recovery-code-input"
                    />
                  ) : (
                    <SixDigitsCode code={code} setCode={setCode} onEnter={handleSubmit} />
                  )}

                  {error && (
                    <DashboardTypography variant="body2" sx={(theme) => ({ color: theme.palette.error.main })}>
                      {error}
                    </DashboardTypography>
                  )}
                </Stack>

                {activeMethod === "totp" && (
                  <Stack alignItems="center">
                    <DashboardLink
                      onClick={toggleRecoveryCode}
                      sx={{ cursor: "pointer" }}
                      data-test="toggle-recovery-code-link"
                    >
                      {isRecoveryCodeMode
                        ? t("dashboard", "twoFactor.totp.useAuthenticatorApp")
                        : t("dashboard", "twoFactor.totp.useRecoveryCode")}
                    </DashboardLink>
                  </Stack>
                )}

                {activeMethod === "totp" && hasSmsFallback && onRequestSmsFallback && (
                  <Stack alignItems="center">
                    <DashboardLink
                      onClick={handleRequestSmsFallback}
                      sx={{ cursor: "pointer" }}
                      data-test="receive-text-instead-link"
                    >
                      {isSwitchingToSms ? t("common", "loading") : t("dashboard", "twoFactor.receiveTextInstead")}
                    </DashboardLink>
                  </Stack>
                )}

                {activeMethod === "sms" && currentUser && (
                  <DashboardTooltip
                    title={t("dashboard", "settings.twoFactorAuthBySmsModal.setup.code.resend.tooltip")}
                    placement="bottom"
                    PopperProps={{ sx: { zIndex: TWO_FACTOR_MODAL_Z_INDEX + 1 } }}
                  >
                    <ResendCodeButton />
                  </DashboardTooltip>
                )}

                <Stack direction="row" justifyContent="center" spacing={0.5}>
                  <DashboardTypography variant="body2">
                    {t("dashboard", "settings.twoFactorAuthBySmsModal.setup.code.help")}
                  </DashboardTypography>
                  <ContactSupportLink />
                </Stack>

                {connectedAsAdministrator && (
                  <DashboardCheckbox
                    data-test="admin-bypass-mfa-checkbox"
                    checked={askForBypassMfa}
                    onChange={(_event, checked) => setAskForBypassMfa(checked)}
                    label={t("dashboard", "twoFactor.adminBypass.label")}
                    helperText={t("dashboard", "twoFactor.adminBypass.helperText")}
                  />
                )}
              </Stack>
            </DashboardDialogContent>
          ),
          footer: (
            <DashboardDialogFooter>
              <DashboardButton
                data-test="submit-code-button"
                fullWidth
                type="submit"
                onClick={handleSubmit}
                isLoading={isSubmitting}
                disabled={!canSubmit || isSubmitting}
              >
                {t("common", "smsCode.submit")}
              </DashboardButton>
              <DashboardButton fullWidth onClick={handleCancel} data-test="cancel-verification-button">
                {t("common", "cancel")}
              </DashboardButton>
            </DashboardDialogFooter>
          ),
        }}
      />
    </DashboardDialog>
  );
};
